<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { getDistanceFromLatLonInMiles } from '@/lib/geography_helper.js'
import { useIntelichekFetch } from '@/lib/intelichek-fetch.js'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useModalStore } from '@/stores/modal.js'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faLocationDot as fasLocationDot, faPhone as fasPhone } from '@fortawesome/free-solid-svg-icons'
import { faEye as farEye } from '@fortawesome/free-regular-svg-icons'

import BusinessHours from '@/components/service_provider/business_hours.vue'
import ServiceProviderPill from '@/components/service_provider/service_provider_pill.vue'
import ProfileCarousel from '@/views/service_providers/components/profile_carousel.vue'
import Map from '@/components/map/map.vue'

// Stores
const router = useRouter()
const route = useRoute()
const authenticationStore = useAuthenticationStore()
const modalStore = useModalStore()

// I18n
const { t } = useI18n()

// Variables
const serviceProvider = computed(() => {
  if (!serviceProviderData?.value?.data?.attributes) return {}
  return serviceProviderData.value.data.attributes
})
const userLocation = computed(() => {
  // Check route query first, then localStorage
  if (route.query.lat && route.query.lng) {
    return {
      latitude: parseFloat(route.query.lat),
      longitude: parseFloat(route.query.lng)
    }
  } else if (typeof localStorage !== 'undefined') {
    const savedLocation = localStorage.getItem('user_location')
    if (savedLocation) {
      try {
        const location = JSON.parse(savedLocation)
        return {
          latitude: location.latitude,
          longitude: location.longitude
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }
  }
  return null
})

const serviceProviderDistance = computed(() => {
  const lat1 = serviceProvider.value?.latlng?.[0]
  const lng1 = serviceProvider.value?.latlng?.[1]
  
  if (!lat1 || !lng1 || !userLocation.value) return '0.0'
  
  return getDistanceFromLatLonInMiles(lat1, lng1, userLocation.value.latitude, userLocation.value.longitude).toFixed(1)
})
const url = computed(() => `${import.meta.env.VITE_BACKEND_URL}/api/v2/service_providers/${route.params.id}`)
const {
  isFetching: isServiceProviderLoading,
  data: serviceProviderData
} = useIntelichekFetch(url, { refetch: true, immediate: true }).json()

// Initialize with empty structure to prevent null errors
if (!serviceProviderData.value) {
  serviceProviderData.value = { data: { attributes: {} } }
}

const websiteDomain = computed(() => {
  if (!serviceProvider.value.website) return ''
  try {
    return new URL(serviceProvider.value.website).hostname
  } catch {
    return serviceProvider.value.website
  }
})

const relatedServiceProvidersWithDistance = computed(() => {
  if (!serviceProvider.value.related_service_providers) return []
  
  return serviceProvider.value.related_service_providers.map(provider => {
    const distance = userLocation.value && provider.latlng ? 
      getDistanceFromLatLonInMiles(provider.latlng[0], provider.latlng[1], userLocation.value.latitude, userLocation.value.longitude).toFixed(1) : '0.0'
    
    return {
      ...provider,
      distance_to_location: parseFloat(distance),
      address: `${provider.street_address}, ${provider.city}, ${provider.state_abbreviation}`
    }
  })
})

const mapCenter = computed(() => {
  if (userLocation.value && serviceProvider.value.latlng) {
    const spLat = serviceProvider.value.latlng[0]
    const spLng = serviceProvider.value.latlng[1]
    return [(userLocation.value.latitude + spLat) / 2, (userLocation.value.longitude + spLng) / 2]
  }
  return serviceProvider.value.latlng
})
</script>

<template>
  <div v-if="isServiceProviderLoading" class="flex justify-center items-center h-64">
    <div class="inline-block h-16 w-16 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white" role="status">
      <span class="sr-only">{{ t('my_garage.loading') }}</span>
    </div>
  </div>
  
  <div v-else class="flex flex-col grow mb-6" :data-service-provider-uuid="serviceProvider?.uuid">
    <!-- Mobile Hero Image -->
    <div v-if="serviceProvider?.sponsored_scope_tier > 0 && serviceProvider.hero_image_url" 
         class="w-full h-48 mb-6 rounded-xl overflow-hidden md:hidden">
      <img :src="serviceProvider.hero_image_url" 
           :alt="serviceProvider.name + ' hero image'" 
           class="w-full h-full object-cover">
    </div>
    
    <!-- Header Section -->
    <div class="relative mb-6 rounded-xl overflow-hidden flex md:min-h-[120px] md:max-h-[160px]" 
         style="background-color: #fbf5e9;">
      <!-- Desktop Hero Image (left third only) -->
      <div v-if="serviceProvider?.sponsored_scope_tier > 0 && serviceProvider.hero_image_url" 
           class="w-1/4 shrink-0 relative -m-4 mr-0 rounded-l-xl overflow-hidden hidden md:block">
        <img :src="serviceProvider.hero_image_url" 
             :alt="serviceProvider.name + ' hero image'" 
             class="w-full h-full object-cover">
        <!-- Gradient overlay -->
        <div class="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
      </div>
      
      <div class="flex flex-col md:flex-row gap-6 flex-1 p-4" :class="serviceProvider?.sponsored_scope_tier > 0 && serviceProvider.hero_image_url ? 'md:pl-6' : ''">
        <!-- Service Provider Name -->
        <div class="flex-1 pl-2">
          <h2 class="text-3xl font-bold mb-2 tracking-tight leading-tight pb-2" style="color: #29476b;">{{ serviceProvider.name }}</h2>
        </div>
        
        <!-- Contact Info -->
        <div class="flex flex-col gap-2 md:shrink-0">
          <a class="rounded-full text-white py-2 px-6 hover:opacity-90 whitespace-nowrap"
             :href="`tel:${serviceProvider.phone_number_raw }`"
             style="background-color: #E8834A;">
            <span class="focus:outline-none text-sm font-normal flex flex-row items-center gap-1 h-full">
              <FontAwesomeIcon :icon="fasPhone" />
              {{ serviceProvider.phone_number_formatted }}
            </span>
          </a>
          
          <a v-if="serviceProvider.website" 
             class="rounded-full text-white py-2 px-6 hover:opacity-90 text-center text-sm font-normal whitespace-nowrap"
             :href="serviceProvider.website" target="_blank"
             style="background-color: #E8834A;">
            {{ websiteDomain }}
          </a>
        </div>
      </div>
    </div>
    
    <!-- Details Section (Map, Gallery, Address, Services) -->
    <div class="flex flex-col xl:flex-row gap-6">
      <!-- Static Map -->
      <div class="w-full xl:max-w-[500px] border rounded-xl overflow-hidden shrink-0 shadow-2xl hidden md:block">
        <div class="h-[312px]">
          <Map v-if="serviceProvider.latlng && serviceProvider.latlng[0] && serviceProvider.latlng[1]" :center-override="serviceProvider.latlng" :default-zoom="14" :show-center-marker="true" :show-user-location="false" :show-connection-line="false" :disable-interactions="true" class="h-full w-full"></Map>
          <div v-else class="loading-placeholder h-full w-full flex items-center justify-center" style="background-color: #fbf5e9;">
            <div class="inline-block h-16 w-16 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white" role="status">
              <span class="sr-only">{{ t('my_garage.loading') }}</span>
            </div>
          </div>
        </div>
        <p class="text-xs italic text-gray-500 p-2 bg-gray-50">Blue pin: Service provider location</p>
      </div>

      <div class="flex flex-col md:flex-row xl:flex-row gap-6 flex-1">
        <!-- Address and Hours -->
        <div class="flex flex-col flex-1 max-w-md xl:pl-6 xl:border-l">
          <p v-if="serviceProvider?.latlng" class="mb-2 text-lg">
            {{ t('service_providers.profile.distance', { distance: serviceProviderDistance }) }}
          </p>

          <div class="flex flex-row gap-2 mb-2">
            <FontAwesomeIcon :icon="fasLocationDot" transform="down-2" size="lg" />
            <div class="address">
              <p>
                {{ serviceProvider.street_address }}
              </p>
              <p>
                {{ serviceProvider.city }}, {{ serviceProvider.state_abbreviation }}, {{ serviceProvider.zip_code }}
              </p>
            </div>
          </div>

          <BusinessHours :service-provider="serviceProvider"></BusinessHours>
        </div>
        
        <!-- Mobile HR separator -->
        <hr class="md:hidden" />
        
        <!-- Services -->
        <div class="flex flex-col flex-1 max-w-md xl:pl-6 xl:border-l">
          <h3 class="text-lg font-semibold">{{ t('service_providers.profile.services') }}</h3>
          <p class="times-disclaimer text-[10px] italic mb-4" v-html="t('service_providers.profile.services_disclaimer')"></p>
          <ul class="flex flex-row flex-wrap">
            <li v-for="category in serviceProvider.categories || []" class="m-1 px-3 py-1 bg-gray-100 rounded-full text-sm">{{ category.name }}</li>
          </ul>
        </div>
      </div>
    </div>
    
    <hr class="my-6" />
    
    <!-- Mobile Gallery (above About Us) -->
    <div v-if="serviceProvider.sponsored_scope_tier > 1 && serviceProvider.gallery_images_urls?.length" class="mb-6 text-center md:hidden">
      <h2 class="text-xl font-semibold mb-4 uppercase" style="color: #29476b;">Gallery</h2>
      <ProfileCarousel :service-provider="serviceProvider"></ProfileCarousel>
    </div>
    
    <!-- Desktop Gallery (standalone when no description) -->
    <div v-if="serviceProvider.sponsored_scope_tier > 1 && serviceProvider.gallery_images_urls?.length && !serviceProvider.description" class="mb-8 text-center hidden md:block">
      <h2 class="text-xl font-semibold mb-4 uppercase" style="color: #29476b;">Gallery</h2>
      <ProfileCarousel :service-provider="serviceProvider"></ProfileCarousel>
    </div>
    
    <div v-if="serviceProvider.description" class="mb-8">
      <h2 class="text-xl font-semibold mb-4 uppercase">{{ t('service_providers.profile.about_us') }}</h2>
      
      <!-- Desktop Gallery (floated right) -->
      <div v-if="serviceProvider.sponsored_scope_tier > 1 && serviceProvider.gallery_images_urls?.length" class="float-right ml-6 mb-4 text-center hidden md:block">
        <h3 class="text-lg font-semibold mb-2 uppercase" style="color: #29476b;">Gallery</h3>
        <ProfileCarousel :service-provider="serviceProvider"></ProfileCarousel>
      </div>
      
      <div class="text-gray-700 leading-relaxed whitespace-pre-line">
        {{ serviceProvider.description }}
      </div>
      
      <div class="clear-both"></div>
    </div>

    <div class="mb-4">
      <h3 class="text-lg font-semibold">{{ t('service_providers.profile.related_service_providers') }}</h3>
      <div class="flex flex-col md:flex-row gap-6">
        <ServiceProviderPill
          v-for="relatedServiceProvider in relatedServiceProvidersWithDistance"
          :service-provider="relatedServiceProvider"
          :compact="true" />
      </div>
    </div>
  </div>
</template>


<style lang="scss" scoped>
@import '@/assets/styles/views/service_providers/service_provider_profile_page.scss';
</style>